(() => {
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const lockBody = (locked) => {
    document.body.classList.toggle('mah-overlay-open', locked);
  };

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container)
      .filter((item) => item.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const initMobileMenu = (root = document) => {
    qsa('[data-mah-mobile-menu]', root).forEach((menu) => {
      if (menu.dataset.mahPhase2bReady === 'true') return;
      menu.dataset.mahPhase2bReady = 'true';

      const close = () => {
        menu.removeAttribute('open');
        lockBody(false);
        menu.querySelector('summary')?.focus();
      };

      menu.addEventListener('toggle', () => {
        lockBody(menu.open);
        if (menu.open) requestAnimationFrame(() => menu.querySelector('[data-mah-menu-close]')?.focus());
      });
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        else if (menu.open) trapFocus(menu.querySelector('.mah-mobile-menu__panel'), event);
      });
      qsa('[data-mah-menu-close]', menu).forEach((button) => button.addEventListener('click', close));
      qsa('a[href]', menu).forEach((link) => link.addEventListener('click', () => {
        menu.removeAttribute('open');
        lockBody(false);
      }));
    });
  };

  const initDesktopDropdowns = (root = document) => {
    qsa('[data-mah-dropdown]', root).forEach((dropdown) => {
      if (dropdown.dataset.mahPhase2bReady === 'true') return;
      dropdown.dataset.mahPhase2bReady = 'true';
      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        qsa('[data-mah-dropdown][open]').forEach((other) => {
          if (other !== dropdown) other.removeAttribute('open');
        });
      });
      dropdown.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          dropdown.removeAttribute('open');
          dropdown.querySelector('summary')?.focus();
        }
      });
    });
  };

  const initFilterDrawer = (root = document) => {
    const drawer = root.querySelector?.('[data-mah-filter-drawer]') || document.querySelector('[data-mah-filter-drawer]');
    if (!drawer || drawer.dataset.mahPhase2bReady === 'true') return;
    drawer.dataset.mahPhase2bReady = 'true';
    const panel = drawer.querySelector('.mah-filter-drawer__panel');
    let trigger = null;

    const open = (button) => {
      trigger = button;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockBody(true);
      requestAnimationFrame(() => panel?.querySelector('button, input, select, a')?.focus());
    };
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      lockBody(false);
      trigger?.focus();
    };

    qsa('[data-mah-filter-open]').forEach((button) => button.addEventListener('click', () => open(button)));
    qsa('[data-mah-filter-close]', drawer).forEach((button) => button.addEventListener('click', close));
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      else if (drawer.classList.contains('is-open')) trapFocus(panel, event);
    });
  };

  const initSort = (root = document) => {
    qsa('[data-mah-sort-select]', root).forEach((select) => {
      if (select.dataset.mahPhase2bReady === 'true') return;
      select.dataset.mahPhase2bReady = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  const initCartQuantities = (root = document) => {
    qsa('[data-mah-cart-form]', root).forEach((form) => {
      if (form.dataset.mahPhase2bReady === 'true') return;
      form.dataset.mahPhase2bReady = 'true';
      let timer;
      const scheduleUpdate = () => {
        clearTimeout(timer);
        timer = setTimeout(() => form.requestSubmit(form.querySelector('[data-mah-cart-update]')), 500);
      };

      qsa('[data-mah-cart-line]', form).forEach((line) => {
        const input = line.querySelector('[data-mah-cart-qty]');
        if (!input) return;
        line.querySelector('[data-mah-cart-minus]')?.addEventListener('click', () => {
          input.value = String(Math.max(Number(input.min || 0), Number(input.value || 0) - 1));
          scheduleUpdate();
        });
        line.querySelector('[data-mah-cart-plus]')?.addEventListener('click', () => {
          input.value = String(Math.max(1, Number(input.value || 0) + 1));
          scheduleUpdate();
        });
        input.addEventListener('change', scheduleUpdate);
      });
    });
  };

  const initAttributeNotes = (root = document) => {
    qsa('[data-mah-attribute-toggle]', root).forEach((button) => {
      if (button.dataset.mahPhase2bReady === 'true') return;
      button.dataset.mahPhase2bReady = 'true';
      const targetId = button.getAttribute('aria-controls');
      const target = targetId ? document.getElementById(targetId) : button.closest('.mah-attribute-wrap')?.querySelector('[data-mah-attribute-note]');
      if (!target) return;
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        target.hidden = expanded;
      });
    });
  };

  const setCurrentNav = (root = document) => {
    const current = new URL(window.location.href);
    qsa('.mah-site-nav a[href], .mah-mobile-menu a[href]', root).forEach((link) => {
      try {
        const target = new URL(link.href, window.location.origin);
        if (target.pathname === current.pathname && !target.hash) link.setAttribute('aria-current', 'page');
      } catch (_) {
        /* Keep original navigation state. */
      }
    });
  };



  const initProductRotators = (root = document) => {
    qsa('[data-mah-product-rotator]', root).forEach((rotator) => {
      if (rotator.dataset.mahRotatorReady === 'true') return;
      rotator.dataset.mahRotatorReady = 'true';
      const slides = qsa('[data-mah-product-slide]', rotator);
      const dots = qsa('[data-mah-rotator-dot]', rotator);
      if (slides.length < 2) return;

      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = null;
      let manuallyPaused = false;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const toggle = rotator.querySelector('[data-mah-rotator-toggle]');

      const setFocusable = (slide, active) => {
        qsa('a, button, input, select, textarea, [tabindex]', slide).forEach((item) => {
          if (active) item.removeAttribute('tabindex');
          else item.setAttribute('tabindex', '-1');
        });
      };

      const show = (nextIndex, userInitiated = false) => {
        index = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          setFocusable(slide, active);
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', String(active));
          dot.tabIndex = active ? 0 : -1;
        });
        if (userInitiated) restart();
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };
      const start = () => {
        stop();
        if (!reducedMotion && !manuallyPaused) timer = window.setInterval(() => show(index + 1), 6000);
      };
      const restart = () => {
        stop();
        start();
      };

      rotator.querySelector('[data-mah-rotator-prev]')?.addEventListener('click', () => show(index - 1, true));
      rotator.querySelector('[data-mah-rotator-next]')?.addEventListener('click', () => show(index + 1, true));
      dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => show(dotIndex, true)));
      toggle?.addEventListener('click', () => {
        manuallyPaused = !manuallyPaused;
        toggle.setAttribute('aria-pressed', String(manuallyPaused));
        toggle.textContent = manuallyPaused ? 'Play' : 'Pause';
        if (manuallyPaused) stop();
        else start();
      });
      rotator.addEventListener('mouseenter', stop);
      rotator.addEventListener('mouseleave', start);
      rotator.addEventListener('focusin', stop);
      rotator.addEventListener('focusout', (event) => {
        if (!rotator.contains(event.relatedTarget)) start();
      });
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      show(index);
      start();
    });
  };

  const init = (root = document) => {
    initMobileMenu(root);
    initDesktopDropdowns(root);
    initFilterDrawer(root);
    initSort(root);
    initCartQuantities(root);
    initAttributeNotes(root);
    // Product rotation is initialised by mah-v4.js.
    setCurrentNav(root);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
